import React, {FC, useCallback} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../../libs/shared/core/src/helpers/internationalization/react';
import {AiReviewConversationsHarvestingJob} from '../../../../../libs/shared/core/src/models/api/jobModel';
import {computeJobProgress} from '../../../../../libs/shared/core/src/models/helpers/jobHelpers';
import {jobDeletedAction} from '../../../../../libs/shared/core/src/store/data/jobs/commonJobsActions';
import {Banner, BannerTypesEnum} from '../../../../../libs/shared/ui/src/components/banners/banner';
import {BannerAction} from '../../../../../libs/shared/ui/src/components/banners/bannerAction';
import {BannerCloseAction} from '../../../../../libs/shared/ui/src/components/banners/bannerCloseAction';
import {BannerContent} from '../../../../../libs/shared/ui/src/components/banners/bannerContent';
import {BannerFooterActions} from '../../../../../libs/shared/ui/src/components/banners/bannerFooterActions';
import {BannerIcon} from '../../../../../libs/shared/ui/src/components/banners/bannerIcon';
import {BannerProgress} from '../../../../../libs/shared/ui/src/components/banners/bannerProgress';
import {BannerPrioritiesEnum} from '../../../../../libs/shared/ui/src/components/banners/bannersContext';
import {useUserTheme} from '../../../../../libs/shared/ui/src/theme/userThemeProvider';
import {useAppDispatch} from '../../../../helpers/appReduxHelpers';
import {buildAiReviewLensPath} from '../../../../helpers/models/lensHelpers';
import {setExpandItemAction} from '../../../../store/data/localPreferences/localPreferencesActions';
import {lensesWorkspaceExpandedKey} from '../../../../store/data/workspace/workspaceExpandedSelectors';
import {navigateToLinkAction} from '../../../../store/stateless/navigationActions';

/*
 * Props.
 */

interface AiReviewConversationsHarvestingBannerProps {
  job: AiReviewConversationsHarvestingJob;
  onDismissClick: (jobId: number) => void;
}

/*
 * Styles.
 */

const StyledBanner = styled(Banner)`
  width: max-content;
`;

/*
 * Component.
 */

export const AiReviewConversationsHarvestingBanner: FC<AiReviewConversationsHarvestingBannerProps> = (
  props,
) => {
  const {job, onDismissClick} = props;
  const progress = computeJobProgress(job);

  const theme = useUserTheme();

  const dispatch = useAppDispatch();

  const onReviewClick = useCallback(() => {
    dispatch(navigateToLinkAction(buildAiReviewLensPath(job.aiReviewLensId)));
    dispatch(setExpandItemAction(lensesWorkspaceExpandedKey, true));

    if (progress === 1) {
      dispatch(jobDeletedAction(job.id));
    }
  }, [dispatch, job.aiReviewLensId, job.id, progress]);

  return (
    <StyledBanner
      bannerType={BannerTypesEnum.MULTILINE}
      priority={BannerPrioritiesEnum.INFORMATION}
      color={progress === 1 ? theme.banner.successBackground : theme.banner.neutralBackground}
    >
      <BannerProgress progress={progress} />
      <BannerCloseAction onClick={() => onDismissClick(job.id)} />
      <BannerIcon iconName={progress === 1 ? 'checkmarkCircleSmall' : 'magicWandSmall'} />
      <BannerContent>
        {progress === 1 ? (
          <Trans id="banner-ai-review-conversations-harvesting-success">Done grabbing messages</Trans>
        ) : (
          <Trans id="banner-ai-review-conversations-harvesting">AI identifying messages to train on...</Trans>
        )}
      </BannerContent>
      {maybeRenderBannerAction(progress, onReviewClick)}
    </StyledBanner>
  );
};

/*
 * Helpers.
 */

function maybeRenderBannerAction(progress: number, onReviewClick: () => void) {
  if (progress === 0) {
    return null;
  }

  return (
    <BannerFooterActions>
      <BannerAction onClick={onReviewClick}>
        <Trans id="banner-ai-review-conversations-harvesting-cta">Go to view</Trans>
      </BannerAction>
    </BannerFooterActions>
  );
}
